import type { Audience, PriceValue, PricingContent } from '../pricing';

/**
 * Arabic pricing page content.
 *
 * Row labels and group titles come from section 14 of the nsave Arabic
 * Localization Pack. The following were NOT in the pack and are translated here —
 * flag them first in a native review:
 *   • the per-month cadence suffix
 *   • every tooltip (`info`) string
 *   • the whole Business table (setup fee, FX row, SWIFT min/max, "Coming soon")
 *
 * ⚠️ The pack's section 2 records unresolved *pricing* conflicts (Pro SWIFT listed
 * as both $3.50 and $9.99; EUR account price differing between site and Help
 * Centre). The figures below mirror the English table verbatim and are deliberately
 * not reinterpreted — Product still owns the canonical numbers.
 *
 * Amounts, percentages and rail names stay in Latin script per the pack's currency
 * rule; `PricingTable` renders every value inside `dir="ltr"` so they read
 * correctly inside RTL rows.
 */

const free: PriceValue = { text: 'مجانًا', free: true };

const receiveInfo = 'من غير رسوم على استقبال USD عبر SWIFT أو ACH.';
const withdrawInfo = 'التحويل لحساب بنكي محلي مربوط بحسابك.';
const rewardsInfo = 'نسبة متغيرة، تُضاف يوميًا.';
// Rendered directly after the amount ("$9.99" + "/شهر"), mirroring the English
// "/m". ⚠️ Authored here — the pack has no cadence string; flag it in review.
const perMonth = '/شهر';

const personal: PricingContent = {
  plans: [
    { name: 'Standard', price: '$0.00', cadence: perMonth },
    { name: 'Pro', price: '$9.99', cadence: perMonth },
  ],
  groups: [
    {
      title: 'الحسابات',
      rows: [
        { label: 'حساب USD', values: [free, free] },
        {
          label: 'بيانات ACH لحساب USD: رقم التوجيه ورقم الحساب',
          values: [free, free],
        },
        { label: 'بيانات التحويل الدولي لحساب USD: IBAN', values: [{ text: '$5.99' }, free] },
        { label: 'حساب GBP', values: [{ text: '$5.99' }, free] },
        { label: 'حساب EUR', values: [{ text: '$5.99' }, free] },
        { label: 'رسوم حسابات USD (IBAN) وGBP وEUR', values: [{ text: '$0.99 شهريًا' }, free] },
      ],
    },
    {
      title: 'استقبال الأموال',
      rows: [
        {
          label: 'استقبال USD عبر SWIFT أو ACH',
          values: [
            { text: 'مجانًا', free: true, info: receiveInfo },
            { text: 'مجانًا', free: true, info: receiveInfo },
          ],
        },
        { label: 'إيداع بعملة مستقرة', values: [free, free] },
      ],
    },
    {
      title: 'إرسال الأموال',
      rows: [
        { label: 'تحويلات بين مستخدمي nsave', values: [free, free] },
        {
          label: 'تحويل لعملتك المحلية',
          values: [
            { text: 'مجانًا', free: true, info: withdrawInfo },
            { text: 'مجانًا', free: true, info: withdrawInfo },
          ],
        },
        {
          label: 'إرسال USD دوليًا عبر ACH',
          values: [{ text: '$0.5' }, { text: '$0.5' }],
        },
        {
          label: 'إرسال USD دوليًا عبر SWIFT',
          values: [{ text: '$12.99' }, { text: '$9.99', highlight: true }],
        },
        { label: 'إرسال GBP عبر FPS', values: [{ text: '$1' }, { text: '$1' }] },
        { label: 'إرسال EUR عبر SEPA', values: [{ text: '$1' }, { text: '$1' }] },
        { label: 'إرسال لمحفظة عملات مستقرة', values: [{ text: '1%' }, { text: '1%' }] },
      ],
    },
    {
      title: 'الإرسال إلى مصر',
      rows: [
        {
          label: 'إرسال USD لحساب بنكي في مصر',
          values: [{ text: '$3.50' }, { text: '$3.50' }],
        },
      ],
    },
    {
      title: 'الكروت',
      cards: true,
      rows: [
        // ⚠️ The Platinum/Infinite tier names are not in the localization pack.
        // They stay in Latin script like the other proper names in this table
        // (SWIFT, ACH, SEPA, IBAN, nsave) rather than being transliterated, so a
        // native review only has to sign off the surrounding phrasing.
        {
          label: 'رسوم إصدار كارت Platinum مرة واحدة',
          values: [{ text: '$1.99' }, free],
        },
        {
          label: 'رسوم إصدار كارت Infinite مرة واحدة',
          values: [{ text: '$99.99' }, { text: '$99.99' }],
        },
        { label: 'الدفع بالكارت بعملة USD', values: [free, free] },
      ],
    },
    {
      title: 'رسوم تحويل العملات',
      rows: [
        {
          label: 'الدفع بالكارت بعملة مختلفة عن عملة رصيد الكارت',
          values: [{ text: '1.5%' }, { text: '1.5%' }],
          cards: true,
        },
        {
          label: 'التحويل بين USD وGBP',
          values: [{ text: '1%' }, { text: '0.5%', down: true }],
        },
      ],
    },
    {
      title: 'المكافآت',
      rows: [
        {
          label: 'مكافآت سنوية على رصيد USD تُضاف يوميًا',
          values: [
            { text: '3.2%', info: rewardsInfo },
            { text: '4.2%', info: rewardsInfo },
          ],
        },
      ],
    },
    {
      title: 'الاستثمار',
      rows: [{ label: 'رسوم تنفيذ الأمر', values: [free, free] }],
    },
  ],
};

const business: PricingContent = {
  plans: [{ name: 'Standard', price: '$0.00', cadence: perMonth }],
  groups: [
    {
      title: 'الحسابات',
      rows: [
        { label: 'رسوم فتح الحساب', values: [{ text: '$200.00' }] },
        { label: 'بيانات ACH لحساب USD: رقم التوجيه ورقم الحساب', values: [free] },
        { label: 'بيانات التحويل الدولي لحساب USD: IBAN', values: [free] },
        { label: 'حساب GBP', values: [free] },
        { label: 'حساب EUR', values: [free] },
        { label: 'تحويل العملات (GBP ↔ EUR ↔ USD)', values: [{ text: '0.6%' }] },
      ],
    },
    {
      title: 'إرسال الأموال',
      rows: [
        { label: 'إرسال GBP عبر FPS', values: [{ text: '$1' }] },
        {
          label: 'إرسال USD أو EUR عبر SWIFT',
          // Amounts are written "14.99 دولار" rather than "$14.99" on purpose: a
          // leading "$" inside an Arabic sentence gets reordered by the bidi
          // algorithm and ends up detached from its number (and drags the closing
          // bracket with it). Spelling the currency avoids the mixed-direction run.
          values: [{ text: '0.3% ** (الحد الأدنى 14.99 دولار، الحد الأقصى 24.99 دولار)' }],
        },
      ],
    },
    {
      title: 'المكافآت',
      rows: [
        {
          label: 'مكافآت سنوية على رصيد USD (قريبًا)',
          values: [{ text: '3.2%', info: rewardsInfo }],
        },
      ],
    },
  ],
};

export const pricing: Record<Audience, PricingContent> = { personal, business };
